import { ReactNode, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import ConfettiExplosion from 'react-confetti-explosion'
import styled from 'styled-components'
import UploadTask from '../Upload'
import TaskBody from '../../TaskBody'
import { SubTask } from '../../../../page_components/PoolDetails/Missions/types'
import { selectCurrentProject } from '../../../../modules/missionControl/selectors'
import {
  InstagramSample,
  SpotifySample,
  TikTokSample,
  YoutubeSample,
} from '../../assets'
import AnimatedLoader from '../../../AnimatedLoader'
import { ModalErrorMessage } from '../../../EmbeddableModal'
import { Box } from '../../../UI/Box'
import Image from '../../../Image'

type Props = {
  subtask: SubTask
  taskId: number
  onClose: (isError?: boolean) => void
  updateTaskSubmissions: () => Promise<unknown>
  taskAmount: ReactNode
}

const ListItem = styled.li`
  color: ${({ theme }) => theme.colors.error};
  list-style: none;
  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 900;
  line-height: 23px;
  letter-spacing: 0.16px;
`

export const BackButtonContainer = styled.div<{ isClose?: boolean }>`
  position: absolute;
  top: -0.5rem;
  ${({ isClose }) => (isClose ? 'right' : 'left')}: 0;
  button {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    width: 32px;
    height: 32px;
    max-width: 32px;
    min-height: 32px;
    padding: 0;
    border: none;
    img {
      z-index: 9;
    }
  }
  cursor: pointer;
`

const BlurredImageContainer = styled.section`
  border-radius: 8px;
  border-width: 2px;
  border: none;
  position: relative;
  width: 100%;
  height: 240px;

  overflow: hidden;

  ::before {
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    backdrop-filter: blur(5px);
    pointer-events: none;
    overflow: inherit;
    border-radius: inherit;
  }

  .image {
    object-fit: contain;
    width: 100%;
    height: 100%;
  }
`

export const Success = styled(Box)``

const ImageVerificationPage = ({
  taskId,
  subtask,
  onClose,
  updateTaskSubmissions,
  taskAmount,
}: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const [errors, setErrors] = useState<string[]>([])
  const currentProject = useSelector(selectCurrentProject)
  const task = currentProject?.tasks.find((task) => task.id === taskId)

  const hasError = !!errors?.length

  const { id, publicName, accountHandleOrTweetUrl, imageVerification } = subtask

  const { instruction, sampleImage, subHeading, subHeadingError } = useMemo(
    () => getModalData(imageVerification, accountHandleOrTweetUrl),
    [imageVerification, accountHandleOrTweetUrl],
  )

  if (!imageVerification || !accountHandleOrTweetUrl) return <></>

  return (
    <TaskBody
      taskId={taskId}
      title={
        isSuccess
          ? 'Mission complete!'
          : isSubmitting
          ? 'Validating screenshot'
          : publicName
      }
      description={
        isSuccess
          ? 'Congratulations'
          : isSubmitting
          ? "This won't take long"
          : instruction
      }
      extra={
        <>
          {isSuccess ? (
            <ConfettiExplosion zIndex={10000} />
          ) : isSubmitting ? (
            <div>
              <AnimatedLoader size={48} onlyWhite />
            </div>
          ) : (
            <Box c dc g={16}>
              <BlurredImageContainer>
                <Image
                  width={400}
                  height={400}
                  src={sampleImage}
                  alt="screen shot sample"
                  className="image"
                />
              </BlurredImageContainer>
              {!hasError && <p className="sub-heading">{subHeading}</p>}
            </Box>
          )}
          <>{taskAmount}</>
        </>
      }
      error={
        hasError ? (
          errors.length ? (
            <ul>
              {errors.map((error, index) => (
                <ListItem key={index}>
                  <ModalErrorMessage>{error}</ModalErrorMessage>
                </ListItem>
              ))}
            </ul>
          ) : (
            <ModalErrorMessage>{subHeadingError}</ModalErrorMessage>
          )
        ) : null
      }
      actions={
        <UploadTask
          task={task?.subtasks?.[0] as SubTask}
          setIsSuccess={setIsSuccess}
          isSubmitting={isSubmitting}
          hasError={hasError}
          setIsSubmitting={setIsSubmitting}
          taskId={taskId}
          subtaskId={id}
          channelName={accountHandleOrTweetUrl}
          setErrors={setErrors}
          type={getTaskType(imageVerification)}
          onClose={onClose}
          isVisible={!isSubmitting && !isSuccess}
          updateTaskSubmissions={updateTaskSubmissions}
        />
      }
    />
  )
}

export default ImageVerificationPage

type ImageValidationType =
  | 'youtube_verification'
  | 'spotify_verification'
  | 'tiktok_verification'
  | 'instagram_verification'

function getTaskType(imageVerification: ImageValidationType) {
  switch (imageVerification) {
    case 'youtube_verification':
      return 'youtube'
    case 'spotify_verification':
      return 'spotify'
    case 'tiktok_verification':
      return 'tiktok'
    case 'instagram_verification':
      return 'instagram'
    default:
      return 'youtube'
  }
}

function getModalData(
  imageVerification: ImageValidationType,
  accountHandleOrTweetUrl: string,
) {
  if (!imageVerification) return

  const modalData: Record<string, any> = {
    youtube: {
      sampleImage: YoutubeSample,
      subHeading:
        'Please make sure the subscribed button and the channel handle are clearly visible',
      subHeadingError: 'We were unable to validate your subscription',
      instruction: (
        <>
          Please upload a screenshot of you subscribed to{' '}
          <strong>{accountHandleOrTweetUrl}</strong> on YouTube.
        </>
      ),
    },
    spotify: {
      sampleImage: SpotifySample,
      subHeading:
        'Please make sure the follow button and the Artist or Podcast name are clearly visible',
      subHeadingError:
        'We were unable to validate your subscription. Please try again',
      instruction: (
        <>
          Please upload a screenshot of you following{' '}
          <strong>{accountHandleOrTweetUrl}</strong> on Spotify.
        </>
      ),
    },
    instagram: {
      sampleImage: InstagramSample,
      subHeading:
        'Please make sure the follow button and the Channel name are clearly visible',
      subHeadingError:
        'We were unable to validate your subscription. Please try again',
      instruction: (
        <>
          Please upload a screenshot of you following{' '}
          <strong>{accountHandleOrTweetUrl}</strong> on Instagram.
        </>
      ),
    },
    tiktok: {
      sampleImage: TikTokSample,
      subHeading:
        "Please make sure the follow button and the user's handle are clearly visible",
      subHeadingError:
        'We were unable to validate your subscription. Please try again',
      instruction: (
        <>
          Please upload a screenshot of you following{' '}
          <strong>{accountHandleOrTweetUrl}</strong> on TikTok.
        </>
      ),
    },
  }

  return modalData[getTaskType(imageVerification)]
}
